import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface AsyncState<T> {
  /** Last successful result (kept while reloading). */
  data: T | undefined;
  /** Error of the current request, if it failed. */
  error: unknown;
  loading: boolean;
  /** Runs the loader again. */
  reload: () => void;
}

interface Settled<T> {
  request: object;
  data?: T;
  error?: unknown;
  failed: boolean;
}

/**
 * Loads data for a page or pane and tracks loading/error state (DESIGN.md §11 state matrix).
 * The loader re-runs whenever `key` changes (pass a string/number identifying the request,
 * e.g. a folder id); stale responses are ignored. While `enabled` is false nothing is requested (lazy loading;
 * the request starts when it becomes true).
 */
export function useAsync<T>(loader: () => Promise<T>, key: string | number = 0, enabled = true): AsyncState<T> {
  const [generation, setGeneration] = useState(0);
  const [settled, setSettled] = useState<Settled<T>>();
  const [data, setData] = useState<T>();
  const loaderRef = useRef(loader);
  useEffect(() => {
    loaderRef.current = loader;
  });

  // A new identity per request; `loading` is simply "the current request has not settled".
  // eslint-disable-next-line react-hooks/exhaustive-deps -- key and generation identify the request
  const request = useMemo(() => ({}), [key, generation]);

  useEffect(() => {
    if (!enabled) return;
    let current = true;
    loaderRef.current().then(
      (result) => {
        if (!current) return;
        setData(result);
        setSettled({ request, failed: false });
      },
      (reason: unknown) => {
        if (current) setSettled({ request, error: reason ?? new Error('Unknown error'), failed: true });
      },
    );
    return () => {
      current = false;
    };
  }, [request, enabled]);

  const reload = useCallback(() => setGeneration((n) => n + 1), []);
  const isCurrent = settled?.request === request;
  return {
    data,
    error: isCurrent && settled.failed ? settled.error : undefined,
    loading: !isCurrent,
    reload,
  };
}
